import { OidcClient, WebStorageStateStore } from 'oidc-client-ts';
import { z } from 'zod';

import { config } from '@/config/config';
import type { OAuthSignInRequest } from '@/modules/auth/oauthCallbackFailure';
import type {
  AuthIntent,
  SlackLoginState,
} from '@/modules/auth/types/externalLogin';
import { sanitizeReturnTo } from '@/modules/auth/utils/sanitizeReturnTo';

const slackAuthority = 'https://slack.com';
const slackAuthorizeEndpoint = 'https://slack.com/openid/connect/authorize';
const slackScope = 'openid email profile';

const { slackClientId, slackRedirectUri } = config.external;

/**
 * The button shows only where both env vars are set (README). The client ID is
 * the Finlay bot's Slack app, which the API also signs in with.
 */
export const isSlackSsoConfigured = Boolean(slackClientId && slackRedirectUri);

const slackStateSchema = z.object({
  flow: z.literal('slack'),
  returnTo: z.string(),
  // Slack's redirect drops the page the user clicked on, so whether its
  // Terms Notice was shown and which page started the flow ride in the state.
  acceptTerms: z.boolean(),
  intent: z.enum(['signUp', 'signIn']),
});

export type SlackSsoErrorCode =
  | 'not_configured'
  | 'cancelled'
  | 'callback_error'
  | 'missing_code'
  | 'missing_state'
  | 'invalid_state'
  | 'invalid_state_data';

export class SlackSsoError extends Error {
  public readonly code: SlackSsoErrorCode;

  constructor(code: SlackSsoErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

interface CompleteSlackLoginResult {
  returnTo: string;
  code: string;
  /** The URI the authorize request used; the API exchange must send the same. */
  redirectUri: string;
  acceptTerms: boolean;
  intent: AuthIntent;
}

let oidcClient: OidcClient | null = null;

const createOrGetOidcClient = () => {
  if (oidcClient) {
    return oidcClient;
  }

  if (!slackClientId || !slackRedirectUri) {
    throw new SlackSsoError(
      'not_configured',
      'Slack sign-in isn’t available. Use another sign-in method.',
    );
  }

  oidcClient = new OidcClient({
    authority: slackAuthority,
    client_id: slackClientId,
    redirect_uri: slackRedirectUri,
    response_type: 'code',
    scope: slackScope,
    // The API exchanges the code without a `code_verifier`, so PKCE stays
    // off. oidc-client-ts reads this only from the settings: it ignores a
    // `disablePKCE` passed to `createSigninRequest`.
    disablePKCE: true,
    metadata: {
      issuer: slackAuthority,
      authorization_endpoint: slackAuthorizeEndpoint,
    },
    stateStore: new WebStorageStateStore({
      store: window.sessionStorage,
    }),
  });

  return oidcClient;
};

export const startSlackLogin = async (
  returnTo: string | undefined,
  acceptTerms: boolean,
  intent: AuthIntent,
) => {
  const client = createOrGetOidcClient();

  const state: SlackLoginState = {
    flow: 'slack',
    returnTo: sanitizeReturnTo(returnTo),
    acceptTerms,
    intent,
  };

  const signInRequest = await client.createSigninRequest({ state });

  window.location.assign(signInRequest.url);
};

export const completeSlackLoginFromCallback = async (
  callbackUrl: string,
): Promise<CompleteSlackLoginResult> => {
  const callback = new URL(callbackUrl, window.location.origin);
  const searchParams = callback.searchParams;

  const callbackError = searchParams.get('error');
  // Cancelling on Slack's consent screen sends `access_denied` alone.
  if (callbackError === 'access_denied') {
    throw new SlackSsoError('cancelled', 'You cancelled the Slack sign-in.');
  }
  if (callbackError) {
    throw new SlackSsoError(
      'callback_error',
      searchParams.get('error_description') ?? callbackError,
    );
  }

  if (!searchParams.get('state')) {
    throw new SlackSsoError(
      'missing_state',
      'Missing state in Slack callback.',
    );
  }

  if (!searchParams.get('code')) {
    throw new SlackSsoError(
      'missing_code',
      'Missing authorization code in Slack callback.',
    );
  }

  const client = createOrGetOidcClient();
  // oidc-client-ts throws a plain Error when storage holds no sign-in for the
  // state, e.g. the callback opened in another browser.
  const { response, state } = await client
    .readSigninResponseState(callback.toString())
    .catch(() => {
      throw new SlackSsoError(
        'invalid_state',
        'Slack callback state could not be validated.',
      );
    });

  if (!response.code) {
    throw new SlackSsoError(
      'invalid_state',
      'Slack callback state could not be validated.',
    );
  }

  const parsedState = slackStateSchema.safeParse(state.data);
  if (!parsedState.success) {
    throw new SlackSsoError(
      'invalid_state_data',
      'Slack callback state payload is invalid.',
    );
  }

  return {
    code: response.code,
    // The API requires this for Slack (403 without it) and Slack rejects any
    // value other than the authorize request's, so it comes from that request.
    redirectUri: state.redirect_uri,
    returnTo: sanitizeReturnTo(parsedState.data.returnTo),
    acceptTerms: parsedState.data.acceptTerms,
    intent: parsedState.data.intent,
  };
};

// Reads the sign-in the callback answers without validating it, so a failed
// one (e.g. the user cancelled at Slack) can still be reported as a failed
// sign-up or sign-in and restarted. Undefined when the stored state is gone.
export const readSlackSignInRequest = async (
  callbackUrl: string,
): Promise<OAuthSignInRequest | undefined> => {
  try {
    const { state } =
      await createOrGetOidcClient().readSigninResponseState(callbackUrl);
    const parsedState = slackStateSchema.safeParse(state.data);
    return parsedState.success
      ? {
          returnTo: parsedState.data.returnTo,
          acceptTerms: parsedState.data.acceptTerms,
          intent: parsedState.data.intent,
        }
      : undefined;
  } catch {
    return undefined;
  }
};

export const clearSlackSigninState = async () => {
  if (!isSlackSsoConfigured) return;
  await createOrGetOidcClient().clearStaleState();
};
